import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import ThumbnailUpload from '../../components/UI/ThumbnailUpload';
import { ChevronDown } from 'lucide-react';

const BRAND_RED = '#c70039';

const SECTORS = [
  'Électricien',
  'Plâtrier',
  'Peintre en bâtiment',
  'Serrurier',
  'Métallier',
  'Plombier',
  'Menuisier',
  'Carreleur',
  'Maçon',
  'Autre',
];

interface RequesterProfileData {
  companyName: string;
  sector: string;
  sectorOther: string;
  city: string;
  firstName: string;
  lastName: string;
  // optional
  zipCode: string;
  streetAddress: string;
  country: string;
  preferredLanguage: string;
  description: string;
  role: string;
  userPhoto?: string;
  companyLogo?: string;
  // keep naceCode for backward compat (read-only if already set)
  naceCode?: string;
}

const RequesterProfile = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  const [profile, setProfile] = useState<RequesterProfileData>({
    companyName: '',
    sector: '',
    sectorOther: '',
    city: '',
    firstName: '',
    lastName: '',
    zipCode: '',
    streetAddress: '',
    country: 'FR',
    preferredLanguage: 'fr',
    description: '',
    role: '',
    userPhoto: '',
    companyLogo: '',
  });

  useEffect(() => {
    if (!currentUser) return;

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists()) {
          toast.error(t('error.userNotFound'));
          return;
        }
        if (userDoc.data().role !== 'requester') {
          toast.error(t('error.accessDenied'));
          return;
        }

        const docSnap = await getDoc(doc(db, 'requesterProfiles', currentUser.uid));
        if (docSnap.exists()) {
          const data = docSnap.data() as RequesterProfileData & { naceCode?: string };
          // Map legacy naceCode to sector if sector is empty
          setProfile(prev => ({
            ...prev,
            ...data,
            sector: data.sector || data.naceCode || '',
            country: data.country || 'FR',
            preferredLanguage: data.preferredLanguage || 'fr',
          }));
        }
      } catch {
        toast.error(t('error.loading'));
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [currentUser, t]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    const effectiveSector =
      profile.sector === 'Autre' ? profile.sectorOther || 'Autre' : profile.sector;

    if (!profile.companyName || !profile.sector || !profile.city || !profile.firstName || !profile.lastName) {
      toast.error('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    if (profile.sector === 'Autre' && !profile.sectorOther) {
      toast.error('Veuillez préciser votre métier/secteur.');
      return;
    }

    try {
      setSaving(true);
      await setDoc(
        doc(db, 'requesterProfiles', currentUser.uid),
        {
          ...profile,
          sector: effectiveSector,
          userId: currentUser.uid,
          updatedAt: new Date(),
        },
        { merge: true }
      );
      toast.success(t('success.saved'));
      navigate('/requester/dashboard');
    } catch {
      toast.error(t('error.saving'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white transition-colors';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div
          className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2"
          style={{ borderColor: BRAND_RED }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-10 px-4">
      {/* Logo */}
      <div className="mb-6 text-center">
        <img
          src="/Logo_OpportunAi-01.png"
          alt="OpportunAI"
          className="h-12 mx-auto mb-3"
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
        <p className="text-sm text-gray-500">La plateforme des artisans et entrepreneurs</p>
      </div>

      <div className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Header bar */}
        <div style={{ background: BRAND_RED }} className="px-6 py-4">
          <h1 className="text-xl font-bold text-white text-center">Votre profil entreprise</h1>
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-xs text-white/60">Compte</span>
            </div>
            <div className="w-8 h-px bg-white/40" />
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center">
                <span className="text-[10px] font-bold" style={{ color: BRAND_RED }}>2</span>
              </div>
              <span className="text-xs text-white font-medium">Profil</span>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          <p className="text-sm text-gray-500 mb-5">
            Quelques informations essentielles pour démarrer — le reste peut être complété plus tard.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nom de l'entreprise */}
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'entreprise <span style={{ color: BRAND_RED }}>*</span>
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={profile.companyName}
                onChange={handleChange}
                className={inputClass}
                required
              />
            </div>

            {/* Métier / Secteur */}
            <div>
              <label htmlFor="sector" className="block text-sm font-medium text-gray-700 mb-1">
                Métier / Secteur d'activité <span style={{ color: BRAND_RED }}>*</span>
              </label>
              <div className="relative">
                <select
                  id="sector"
                  name="sector"
                  value={profile.sector}
                  onChange={handleChange}
                  className={inputClass + ' appearance-none pr-9'}
                  required
                >
                  <option value="">Sélectionnez votre métier</option>
                  {SECTORS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
            </div>

            {/* Autre — free text */}
            {profile.sector === 'Autre' && (
              <div>
                <label htmlFor="sectorOther" className="block text-sm font-medium text-gray-700 mb-1">
                  Précisez votre métier <span style={{ color: BRAND_RED }}>*</span>
                </label>
                <input
                  type="text"
                  id="sectorOther"
                  name="sectorOther"
                  value={profile.sectorOther}
                  onChange={handleChange}
                  placeholder="Ex: Couvreur, Paysagiste…"
                  className={inputClass}
                  required
                />
              </div>
            )}

            {/* Ville */}
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                Ville <span style={{ color: BRAND_RED }}>*</span>
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={profile.city}
                onChange={handleChange}
                className={inputClass}
                required
              />
            </div>

            {/* Prénom / Nom */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom <span style={{ color: BRAND_RED }}>*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={profile.firstName}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom <span style={{ color: BRAND_RED }}>*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={profile.lastName}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            {/* Optional section toggle */}
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setShowOptional(v => !v)}
                className="flex items-center gap-2 text-sm font-medium transition-colors"
                style={{ color: BRAND_RED }}
              >
                <ChevronDown
                  className="h-4 w-4 transition-transform"
                  style={{ transform: showOptional ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
                {showOptional ? 'Masquer' : 'Compléter mon profil'} (optionnel)
              </button>
            </div>

            {showOptional && (
              <div className="space-y-4 pt-1 border-t border-gray-100">
                <p className="text-xs text-gray-400 pt-2">
                  Ces informations peuvent être ajoutées plus tard depuis votre tableau de bord.
                </p>

                {/* Profile images */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ThumbnailUpload
                    type="user"
                    currentImage={profile.userPhoto}
                    onImageChange={img => setProfile(p => ({ ...p, userPhoto: img || '' }))}
                    disabled={saving}
                  />
                  <ThumbnailUpload
                    type="company"
                    currentImage={profile.companyLogo}
                    onImageChange={img => setProfile(p => ({ ...p, companyLogo: img || '' }))}
                    disabled={saving}
                  />
                </div>

                {/* Address */}
                <div>
                  <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse complète
                  </label>
                  <input
                    type="text"
                    id="streetAddress"
                    name="streetAddress"
                    value={profile.streetAddress}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                {/* Zip */}
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Code postal
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={profile.zipCode}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                {/* Country */}
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Pays
                  </label>
                  <div className="relative">
                    <select
                      id="country"
                      name="country"
                      value={profile.country}
                      onChange={handleChange}
                      className={inputClass + ' appearance-none pr-9'}
                    >
                      <option value="FR">France</option>
                      <option value="BE">Belgique</option>
                      <option value="CH">Suisse</option>
                      <option value="LU">Luxembourg</option>
                      <option value="DE">Germany</option>
                      <option value="GB">United Kingdom</option>
                      <option value="IT">Italy</option>
                      <option value="ES">Spain</option>
                      <option value="NL">Netherlands</option>
                      <option value="AT">Austria</option>
                      <option value="SE">Sweden</option>
                      <option value="PL">Poland</option>
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                </div>

                {/* Language */}
                <div>
                  <label htmlFor="preferredLanguage" className="block text-sm font-medium text-gray-700 mb-1">
                    Langue préférée
                  </label>
                  <div className="relative">
                    <select
                      id="preferredLanguage"
                      name="preferredLanguage"
                      value={profile.preferredLanguage}
                      onChange={handleChange}
                      className={inputClass + ' appearance-none pr-9'}
                    >
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                </div>

                {/* Role in company */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                    Rôle dans l'entreprise
                  </label>
                  <input
                    type="text"
                    id="role"
                    name="role"
                    value={profile.role}
                    onChange={handleChange}
                    placeholder="Ex: Gérant, Associé…"
                    className={inputClass}
                  />
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description de l'entreprise
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={profile.description}
                    onChange={handleChange}
                    rows={3}
                    className={inputClass}
                  />
                </div>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={saving}
              className="w-full py-3 rounded-lg text-white font-semibold text-sm transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: BRAND_RED }}
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Enregistrement…
                </>
              ) : (
                'Accéder à mon espace →'
              )}
            </button>

            <p className="text-center text-xs text-gray-400">
              Vous pourrez compléter votre profil à tout moment depuis votre tableau de bord.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RequesterProfile;
